import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ProceduralAudio } from "./audio/procedural-audio";
import { loadFirebaseCloudGateway } from "./cloud/config";
import type { CloudGateway } from "./cloud/gateway";
import type { MatchMode } from "./game/types";
import type { EndlessCheckpoint } from "./progress/match-persistence";
import { type CosmeticId, type ProgressSettings } from "./progress/schema";
import {
  type OfficialMatchHandle,
  type ProgressSession,
} from "./progress/session";
import { applyTheme } from "./theme/theme";
import { shouldActivatePendingCloudSession } from "./ui/cloud-session-activation";
import { AccountPage } from "./ui/AccountPage";
import { AppearancePage } from "./ui/AppearancePage";
import { HelpPage } from "./ui/HelpPage";
import { Home } from "./ui/Home";
import { MatchView } from "./ui/match/MatchView";
import { ResultDialog } from "./ui/ResultDialog";
import { SettingsPage } from "./ui/SettingsPage";
import { applyMatchResult } from "./app/match-result";
import { cloudConfigured, createSession } from "./app/session-factory";
import type { AssistEnableResult, MatchResult, Screen } from "./app/types";

export default function App() {
  const [session, setSession] = useState(createSession);
  const audio = useMemo(() => new ProceduralAudio(), []);
  const [progress, setProgress] = useState(() => {
    session.start();
    return session.recoverCurrentOwnerEndlessJournal();
  });
  const [screen, setScreen] = useState<Screen>("home");
  const [appearanceReturn, setAppearanceReturn] = useState<"home" | "settings">(
    "home",
  );
  const [selectedMode, setSelectedMode] = useState<MatchMode>("signal");
  const [activeMatch, setActiveMatch] = useState<{
    readonly mode: MatchMode;
    readonly handle: OfficialMatchHandle;
    readonly owner: ProgressSession;
  } | null>(null);
  const activeMatchRef = useRef(activeMatch);
  const sessionUnmountGenerationRef = useRef(0);
  const [pendingCloudGateway, setPendingCloudGateway] =
    useState<CloudGateway | null>(null);
  const activatingCloudGatewayRef = useRef<CloudGateway | null>(null);
  const [matchNumber, setMatchNumber] = useState(0);
  const [result, setResult] = useState<MatchResult | null>(null);
  const [testProgressRefreshes, setTestProgressRefreshes] = useState(0);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [screen]);

  useLayoutEffect(() => {
    applyTheme(progress.progress.settings.theme);
  }, [progress.progress.settings.theme]);

  useLayoutEffect(() => {
    document.documentElement.dataset.reducedMotion = String(
      progress.progress.settings.reducedMotion,
    );
  }, [progress.progress.settings.reducedMotion]);

  const refresh = useCallback(() => setProgress(session.snapshot), [session]);
  const navigate = useCallback(
    (next: Screen) => {
      if (next === "appearance" && screen !== "appearance") {
        setAppearanceReturn(screen === "settings" ? "settings" : "home");
      }
      setScreen(next);
      setResult(null);
    },
    [screen],
  );
  const startMatch = useCallback(
    (requestedMode: MatchMode = selectedMode) => {
      if (activeMatchRef.current) return;
      const sessionId = crypto.randomUUID();
      let handle: OfficialMatchHandle;
      try {
        handle = session.beginOfficialMatch(sessionId, requestedMode);
      } catch {
        setProgress(session.snapshot);
        return;
      }
      const nextActiveMatch = { mode: requestedMode, handle, owner: session };
      activeMatchRef.current = nextActiveMatch;
      setSelectedMode(requestedMode);
      setActiveMatch(nextActiveMatch);
      setResult(null);
      setMatchNumber((value) => value + 1);
      setScreen("match");
      void audio.unlock(progress.progress.settings).catch(() => undefined);
    },
    [audio, progress.progress.settings, selectedMode, session],
  );
  const finishMatch = useCallback(
    (nextResult: MatchResult) => {
      if (!activeMatch) return;
      const next = activeMatch.owner.updateFromMatch(
        activeMatch.handle,
        (draft) => applyMatchResult(draft, nextResult),
      );
      activeMatch.owner.finishOfficialMatch(activeMatch.handle, {
        progressPersisted: next.mode !== "storage-unavailable",
      });
      activeMatchRef.current = null;
      setActiveMatch(null);
      setProgress(next);
      setResult(nextResult);
      audio.cue(
        nextResult.deathReason === "time" ? "tide" : "death",
        next.progress.settings,
      );
      if (
        nextResult.deathReason !== "time" &&
        next.progress.settings.vibration &&
        "vibrate" in navigator
      ) {
        navigator.vibrate([45, 35, 80]);
      }
    },
    [activeMatch, audio],
  );
  const abandonMatch = useCallback(() => {
    if (activeMatch) activeMatch.owner.finishOfficialMatch(activeMatch.handle);
    activeMatchRef.current = null;
    setActiveMatch(null);
    navigate("home");
  }, [activeMatch, navigate]);
  const enableAssist = useCallback((): Promise<AssistEnableResult> => {
    if (!activeMatch || progress.debugEntitlement !== "allowed") {
      return Promise.resolve({
        ok: false,
        message: "当前连接未获得 LEO ASSIST 授权",
      });
    }
    const marked = activeMatch.owner.markMatchAssisted(activeMatch.handle);
    if (!marked.ok) {
      return Promise.resolve({
        ok: false,
        message: "无法持久化并确认 assisted 标记，辅助保持关闭",
      });
    }
    return Promise.resolve({ ok: true });
  }, [activeMatch, progress.debugEntitlement]);
  const checkpointEndless = useCallback(
    (checkpoint: EndlessCheckpoint) => {
      if (!activeMatch || activeMatch.mode !== "endless") return;
      activeMatch.owner.checkpointEndlessMatch(activeMatch.handle, checkpoint);
    },
    [activeMatch],
  );
  const changeSettings = useCallback(
    (settings: ProgressSettings) => {
      const next = session.update((draft) => {
        draft.settings = { ...settings };
      });
      setProgress(next);
      audio.apply(settings);
    },
    [audio, session],
  );
  const equipCosmetic = useCallback(
    (cosmetic: CosmeticId) => {
      const next = session.update((draft) => {
        if (draft.ownedCosmetics[cosmetic] === true) {
          draft.equippedCosmetic = cosmetic;
        }
      });
      setProgress(next);
    },
    [session],
  );

  useEffect(() => () => audio.stop(), [audio]);
  useEffect(() => {
    const generation = ++sessionUnmountGenerationRef.current;
    return () => {
      queueMicrotask(() => {
        if (sessionUnmountGenerationRef.current === generation) {
          session.dispose();
        }
      });
    };
  }, [session]);
  useEffect(() => {
    if (!import.meta.env.DEV) return undefined;
    const onTestProgressRefresh = () => {
      refresh();
      setTestProgressRefreshes((value) => value + 1);
    };
    window.addEventListener(
      "gloamline:test-progress-refresh",
      onTestProgressRefresh,
    );
    return () =>
      window.removeEventListener(
        "gloamline:test-progress-refresh",
        onTestProgressRefresh,
      );
  }, [refresh]);
  useEffect(() => {
    const unsubscribe = session.subscribe(setProgress);
    const handleOnline = () => {
      void session.handleOnline().catch(() => {
        setProgress({
          ...session.snapshot,
          message: "云同步暂不可用；Account Progress 仍安全保存在本地",
        });
      });
    };
    const handleOffline = () => session.handleOffline();
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      unsubscribe();
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [session]);
  useEffect(() => {
    if (!cloudConfigured) return undefined;
    let disposed = false;
    void loadFirebaseCloudGateway(import.meta.env)
      .then((cloud) => {
        if (!cloud || disposed) return;
        setPendingCloudGateway(cloud);
      })
      .catch(() => {
        if (disposed) return;
        setProgress((current) => ({
          ...current,
          message: "云同步初始化失败；Guest Progress 仍保存在当前浏览器",
        }));
      });
    return () => {
      disposed = true;
    };
  }, []);
  useEffect(() => {
    if (
      !shouldActivatePendingCloudSession(
        activeMatch !== null,
        pendingCloudGateway,
        activatingCloudGatewayRef.current,
      )
    ) {
      return undefined;
    }
    let disposed = false;
    let candidateSession: ProgressSession | null = null;
    let adoptedCandidate = false;
    const cloud = pendingCloudGateway;
    activatingCloudGatewayRef.current = cloud;
    const discardCandidate = () => {
      if (!candidateSession || adoptedCandidate) return;
      candidateSession.dispose();
      candidateSession = null;
    };
    void (async () => {
      const connectedSession = createSession(cloud);
      candidateSession = connectedSession;
      connectedSession.start();
      connectedSession.recoverCurrentOwnerEndlessJournal();
      await connectedSession.resume();
      if (disposed || activeMatchRef.current) {
        discardCandidate();
        return;
      }
      const recovered = connectedSession.recoverCurrentOwnerEndlessJournal();
      if (disposed || activeMatchRef.current) {
        discardCandidate();
        return;
      }
      adoptedCandidate = true;
      session.dispose();
      setSession(connectedSession);
      setProgress(recovered);
      setPendingCloudGateway((current) => (current === cloud ? null : current));
      activatingCloudGatewayRef.current = null;
    })().catch(() => {
      discardCandidate();
      if (disposed) return;
      activatingCloudGatewayRef.current = null;
      setPendingCloudGateway((current) => (current === cloud ? null : current));
      setProgress((current) => ({
        ...current,
        message: "云同步初始化失败；Guest Progress 仍保存在当前浏览器",
      }));
    });
    return () => {
      disposed = true;
      discardCandidate();
      if (activatingCloudGatewayRef.current === cloud) {
        activatingCloudGatewayRef.current = null;
      }
    };
  }, [activeMatch, pendingCloudGateway]);

  return (
    <div
      className="app-shell"
      data-test-progress-refreshes={
        import.meta.env.DEV ? testProgressRefreshes : undefined
      }
    >
      <div className="noise" aria-hidden="true" />
      {screen === "home" ? (
        <Home
          progress={progress}
          selectedMode={selectedMode}
          onModeChange={setSelectedMode}
          onStart={() => startMatch(selectedMode)}
          onNavigate={navigate}
        />
      ) : null}
      {screen === "settings" ? (
        <SettingsPage
          onNavigate={navigate}
          progress={progress}
          onBack={() => navigate("home")}
          onChange={changeSettings}
          onAccount={() => navigate("account")}
          onHelp={() => navigate("help")}
          onAppearance={() => navigate("appearance")}
        />
      ) : null}
      {screen === "help" ? (
        <HelpPage
          onNavigate={navigate}
          progress={progress}
          onBack={() => navigate("home")}
        />
      ) : null}
      {screen === "account" ? (
        <AccountPage
          onNavigate={navigate}
          session={session}
          progress={progress}
          onBack={() => navigate("settings")}
          onRefresh={refresh}
        />
      ) : null}
      {screen === "appearance" ? (
        <AppearancePage
          onNavigate={navigate}
          progress={progress}
          onBack={() => navigate(appearanceReturn)}
          onEquip={equipCosmetic}
        />
      ) : null}
      {screen === "match" ? (
        <MatchView
          key={matchNumber}
          mode={activeMatch?.mode ?? selectedMode}
          settings={progress.progress.settings}
          cosmetic={progress.progress.equippedCosmetic}
          audio={audio}
          assistAllowed={progress.debugEntitlement === "allowed"}
          showTutorial={!progress.progress.tutorial.completed}
          resultVisible={result !== null}
          onEnableAssist={enableAssist}
          onCheckpoint={checkpointEndless}
          onFinish={finishMatch}
          onExit={abandonMatch}
        />
      ) : null}
      {result ? (
        <ResultDialog
          result={result}
          onRestart={() => startMatch(result.mode)}
          onHome={() => navigate("home")}
        />
      ) : null}
      {progress.recoveredCorruptProgress && screen !== "match" ? (
        <div className="recovery-banner" role="status">
          损坏的 Guest Progress 已隔离，游戏已使用安全默认值继续。
        </div>
      ) : null}
    </div>
  );
}
